// Vue partition : grave le morceau une fois (et à chaque changement de mains, d'aides ou
// de taille), puis suit la séance sans regraver : le curseur et le défilement sont mis à
// jour à chaque image par une poignée impérative ; les résultats et les notes attendues
// arrivent par les propriétés, donc une vue recréée (retour depuis l'affichage « Notes »)
// reçoit immédiatement l'état complet de la séance, sans attendre une nouvelle frappe.

import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import type { NoteState } from '../../engine/session';
import type { Hand, Score } from '../../music/model';
import type { NamingSystem } from '../../music/pitchNames';
import { buildSheetModel } from '../../music/sheetModel';
import { scrollOffsetFor, xForTick } from '../render/cursorMap';
import { SheetRenderer, naturalSheetHeight, type SheetRenderResult } from '../render/sheetRenderer';

export interface SheetHandle {
  /** Place le curseur (et fait défiler) pour un tick donné. */
  setTick(tick: number): void;
}

interface Props {
  score: Score;
  hands: readonly Hand[];
  showFingers: boolean;
  showNames: boolean;
  naming: NamingSystem;
  /** Résultats de la séance, par identifiant de note. */
  noteStates: ReadonlyMap<string, NoteState>;
  /** Notes attendues à mettre en évidence (mode attente). */
  expectedIds: readonly string[];
  /** Position de lecture courante, appliquée dès la gravure (avant la première image). */
  initialTick?: number;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

export const SheetView = forwardRef<SheetHandle, Props>(function SheetView({ score, hands, showFingers, showNames, naming, noteStates, expectedIds, initialTick = 0 }, ref) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<SheetRenderer | null>(null);
  const resultRef = useRef<SheetRenderResult | null>(null);
  const tickRef = useRef(initialTick);
  const statesRef = useRef(noteStates);
  const expectedRef = useRef(expectedIds);
  statesRef.current = noteStates;
  expectedRef.current = expectedIds;

  const updateHighlight = useCallback(() => {
    const r = resultRef.current;
    const box = highlightRef.current;
    if (!r || !box) return;
    const ids = new Set(expectedRef.current);
    const hits = r.boxes.filter((b) => b.noteIds.some((id) => ids.has(id)));
    if (hits.length === 0) {
      box.hidden = true;
      return;
    }
    const x0 = Math.min(...hits.map((b) => b.x));
    const y0 = Math.min(...hits.map((b) => b.y));
    const x1 = Math.max(...hits.map((b) => b.x + b.w));
    const y1 = Math.max(...hits.map((b) => b.y + b.h));
    const s = r.scale;
    box.hidden = false;
    box.style.left = `${(x0 - 4) * s}px`;
    box.style.top = `${(y0 - 4) * s}px`;
    box.style.width = `${(x1 - x0 + 8) * s}px`;
    box.style.height = `${(y1 - y0 + 8) * s}px`;
  }, []);

  const position = useCallback((tick: number) => {
    const r = resultRef.current;
    const scroll = scrollRef.current;
    const inner = innerRef.current;
    const cursor = cursorRef.current;
    if (!r || !scroll || !inner || !cursor) return;
    const x = xForTick(r.anchors, tick) * r.scale;
    cursor.style.transform = `translateX(${x}px)`;
    const offset = scrollOffsetFor(x, scroll.clientWidth, r.width * r.scale);
    inner.style.transform = `translateX(${-offset}px)`;
  }, []);

  const render = useCallback(() => {
    const host = hostRef.current;
    const scroll = scrollRef.current;
    if (!host || !scroll) return;
    const model = buildSheetModel(score, hands);
    if (!rendererRef.current) rendererRef.current = new SheetRenderer(host);
    const available = scroll.clientHeight;
    const scale = clamp((available - 8) / naturalSheetHeight(model.staves.length), 0.5, 1.8);
    const result = rendererRef.current.render(model, { showFingers, showNames, naming, scale });
    resultRef.current = result;
    host.style.width = `${result.width * scale}px`;
    host.style.height = `${result.height * scale}px`;
    if (cursorRef.current) cursorRef.current.style.height = `${result.height * scale}px`;
    // Centrage vertical quand le volet est plus haut que la gravure.
    if (innerRef.current) innerRef.current.style.top = `${Math.max(0, (available - result.height * scale) / 2)}px`;
    // État complet de la séance, appliqué dès la gravure.
    rendererRef.current.applyStates(statesRef.current);
    rendererRef.current.applyExpected(expectedRef.current);
    updateHighlight();
    position(tickRef.current);
  }, [score, hands, showFingers, showNames, naming, position, updateHighlight]);

  useEffect(() => {
    render();
    const scroll = scrollRef.current;
    if (!scroll) return;
    let lastHeight = scroll.clientHeight;
    const observer = new ResizeObserver(() => {
      if (scroll.clientHeight !== lastHeight) {
        lastHeight = scroll.clientHeight;
        render();
      } else {
        position(tickRef.current);
      }
    });
    observer.observe(scroll);
    return () => observer.disconnect();
  }, [render, position]);

  // Résultats et notes attendues : appliqués sans regraver, à chaque changement.
  useEffect(() => {
    rendererRef.current?.applyStates(noteStates);
  }, [noteStates]);

  useEffect(() => {
    rendererRef.current?.applyExpected(expectedIds);
    updateHighlight();
  }, [expectedIds, updateHighlight]);

  useImperativeHandle(
    ref,
    () => ({
      setTick(tick) {
        tickRef.current = tick;
        position(tick);
      },
    }),
    [position],
  );

  return (
    <div className="sheet-scroll" ref={scrollRef} aria-label="Partition">
      <div className="sheet-inner" ref={innerRef}>
        <div className="sheet-host" ref={hostRef} />
        <div className="sheet-highlight" ref={highlightRef} hidden />
        <div className="sheet-cursor" ref={cursorRef} />
      </div>
    </div>
  );
});
